import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { noteKeys } from './noteKeys';
import {
  NoteData,
  FormattedNote,
  CreateNoteInput,
  UpdateNoteInput,
  formatNote,
  NoteFilters,
} from './noteTypes';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { invalidateQuery, safeUpdateQueryData } from '../../utils/queryInvalidationUtils';
import { applyFilters, sortByCreationDateAsc } from './noteUtils';

/**
 * Hook for fetching all notes for a specific topic
 * Supports offline mode by using IndexedDB when offline
 */
export function useNotes(topicId: string | null, filters?: NoteFilters) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  // Create a query key that includes any filters
  const queryKey = topicId
    ? filters
      ? [...noteKeys.byTopic(topicId), filters]
      : noteKeys.byTopic(topicId)
    : noteKeys.lists();

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(`Starting notes fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`);
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching notes from IndexedDB #${fetchId}`);

          try {
            // Get notes from IndexedDB
            const notes = await db.notes
              .where('topic_id')
              .equals(topicId)
              .and(note => !note.isDeleted) // Filter out deleted notes
              .toArray();

            logger.fetch(`Offline mode: loaded ${notes.length} notes from IndexedDB`);

            // Apply filtering and sorting
            let processedNotes = notes as NoteData[];

            // Apply filters if provided
            if (filters) {
              processedNotes = applyFilters(processedNotes, filters);
            }

            // Sort notes by date (oldest first for consistent sorting)
            const sortedNotes = sortByCreationDateAsc(processedNotes);

            // Format notes for UI
            return sortedNotes.map(note => formatNote(note as NoteData));
          } catch (offlineError) {
            logger.error(`Error fetching offline notes #${fetchId}:`, offlineError);
            throw new Error('Failed to load notes from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('topic_id', topicId)
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Store in IndexedDB for offline access
        if (data && data.length > 0) {
          try {
            await db.notes.bulkPut(data);
            logger.fetch(`Stored ${data.length} notes in IndexedDB`);
          } catch (storageError) {
            logger.error(`Error storing notes in IndexedDB:`, storageError);
            // Continue even if storage fails - this is non-critical
            // In a production app, we might want to show a warning to the user or retry
          }
        }

        // Apply filters if provided
        let processedNotes = data || [];

        if (filters) {
          processedNotes = applyFilters(processedNotes, filters);
        }

        // Format notes using the helper function
        const formattedNotes = processedNotes.map(note => formatNote(note as NoteData));

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Notes fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedNotes;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'toast', // Change to toast for visibility of fetch errors
          context: {
            fetchId,
            operation: 'fetchNotes',
            topicId,
            isOnline,
          },
        });

        throw appError;
      }
    },
    enabled: !!topicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for fetching a single note by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useNote(noteId: string | null) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: noteId ? noteKeys.detail(noteId) : noteKeys.details(),
    queryFn: async () => {
      if (!noteId) {
        throw new Error('Note ID is required');
      }

      logger.fetch(`Fetching note ${noteId} (online: ${isOnline})`);

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching note ${noteId} from IndexedDB`);

          try {
            // Get note from IndexedDB
            const note = await db.notes.get(noteId);

            if (!note || note.isDeleted) {
              throw new Error(`Note ${noteId} not found in offline storage`);
            }

            // Format the note
            return formatNote(note as NoteData);
          } catch (offlineError) {
            logger.error(`Error fetching offline note ${noteId}:`, offlineError);
            throw new Error('Failed to load note from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase.from('notes').select('*').eq('id', noteId).single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.notes.put(data);
        } catch (storageError) {
          logger.error(`Error storing note in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the note
        return formatNote(data as NoteData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast', // Change to toast for consistency with fetchNotes
          context: { operation: 'fetchNote', noteId, isOnline },
        });
        throw appError;
      }
    },
    enabled: !!noteId,
  });
}

/**
 * Hook for creating a new note
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useAddNote() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (noteInput: CreateNoteInput): Promise<FormattedNote> => {
      const { topicId, content, summary = null } = noteInput;

      logger.store('Adding new note', {
        topicId,
        contentLength: content.length,
        hasSummary: !!summary,
        is_online: isOnline,
      });

      try {
        const supabaseClient = await supabase;
        const {
          data: { user },
        } = await supabaseClient.auth.getUser();

        if (!user) {
          throw new Error('No authenticated user');
        }

        // Create note data object with metadata
        const now = new Date().toISOString();
        const noteId = crypto.randomUUID();

        const noteData: NoteData = {
          id: noteId,
          topic_id: topicId,
          user_id: user.id,
          content: content.trim(),
          summary: summary,
          created_at: now,
          updated_at: now,
          metadata: {
            client_id: CLIENT_ID,
            last_update: now,
          },
        };

        // If offline, save locally and queue for sync
        if (!isOnline) {
          // Store in IndexedDB
          await db.notes.put(noteData);

          // Queue for sync when online
          await queueOfflineOperation('notes', 'create', noteId, noteData);

          // Return formatted note
          return formatNote(noteData);
        }

        // Online flow - send to server
        const { data: newNote, error } = await supabaseClient
          .from('notes')
          .insert([noteData])
          .select()
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.notes.put(newNote);
          logger.fetch(`Stored note ${newNote.id} in IndexedDB`);
        } catch (storageError) {
          logger.error(`Error storing note in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
          // In a production app, we might want to show a warning to the user or retry
        }

        // If content is long enough and no summary was provided,
        // generate a summary via Netlify function (asynchronously)
        if (!summary && content.trim().length > 100) {
          try {
            fetch('/.netlify/functions/note-summary', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                batchSize: 1,
                regenerateExisting: false,
                noteIds: [noteId],
              }),
            }).catch(error => {
              logger.error('Error generating summary via Netlify function', error);
            });
          } catch (summaryError) {
            logger.error('Error initiating summary generation', summaryError);
          }
        }

        // Return formatted note
        return formatNote(newNote);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'createNote', topicId },
        });
        throw appError;
      }
    },

    onSuccess: (newNote, variables) => {
      const { topicId } = variables;

      // Invalidate notes list for the topic
      invalidateQuery(queryClient, noteKeys.byTopic(topicId), {
        refetchType: 'active',
        context: { operation: 'createNote', noteId: newNote.id },
      });

      // Add the new note to the detail cache
      safeUpdateQueryData(queryClient, noteKeys.detail(newNote.id), () => newNote, {
        context: { operation: 'createNote', noteId: newNote.id },
      });
    },
  });
}

/**
 * Hook for updating a note
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateNote() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      topicId,
      noteId,
      updates,
    }: {
      topicId: string;
      noteId: string;
      updates: UpdateNoteInput;
    }): Promise<FormattedNote> => {
      if (!noteId) {
        throw new Error('Note ID is required');
      }

      const now = new Date().toISOString();

      // Create update payload
      const updatePayload: Partial<NoteData> = {
        content: updates.content.trim(),
        updated_at: now,
        metadata: {
          client_id: CLIENT_ID,
          last_update: now,
        },
      };

      // If summary is explicitly provided, use it
      if (updates.summary !== undefined) {
        updatePayload.summary = updates.summary;
      }
      // If content is too short for a summary, explicitly set summary to null
      else if (updates.content.trim().length <= 100) {
        updatePayload.summary = null;
      }

      logger.store('Updating note', {
        noteId,
        topicId,
        contentLength: updates.content.length,
        hasSummary: updates.summary !== undefined,
        summaryLength: updates.summary?.length || 0,
        is_online: isOnline,
      });

      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current note from IndexedDB
          const currentNote = await db.notes.get(noteId);

          if (!currentNote || currentNote.isDeleted) {
            throw new Error(`Note ${noteId} not found in offline storage`);
          }

          // Merge updates with current data
          const updatedNote = {
            ...currentNote,
            ...updatePayload,
          };

          // Update in IndexedDB
          await db.notes.put(updatedNote);

          // Queue for sync when online
          await queueOfflineOperation('notes', 'update', noteId, updatedNote);

          // Return formatted note
          return formatNote(updatedNote as NoteData);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateNote', noteId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      try {
        const supabaseClient = await supabase;

        // Get current note to access its metadata
        const { data: currentNote, error: fetchError } = await supabaseClient
          .from('notes')
          .select('metadata')
          .eq('id', noteId)
          .single();

        if (fetchError) throw fetchError;

        // Preserve existing metadata fields
        if (currentNote?.metadata) {
          updatePayload.metadata = {
            ...currentNote.metadata,
            ...updatePayload.metadata,
          };
        }

        // Update the note
        const { data, error } = await supabaseClient
          .from('notes')
          .update(updatePayload)
          .eq('id', noteId)
          .select()
          .single();

        if (error) throw error;

        // Update in IndexedDB for offline access
        try {
          // Get current data first to preserve fields not included in the update
          const existingNote = await db.notes.get(noteId);

          if (existingNote) {
            await db.notes.put({
              ...existingNote,
              ...data,
            });
            logger.fetch(`Updated note ${noteId} in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error updating note in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
          // In a production app, we might want to show a warning to the user or retry
        }

        // If content is long enough but has no summary now, request summary generation
        if (updates.summary === undefined && updates.content.trim().length > 100 && !data.summary) {
          try {
            fetch('/.netlify/functions/note-summary', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                batchSize: 1,
                regenerateExisting: true,
                noteIds: [noteId],
              }),
            }).catch(error => {
              logger.error('Error generating summary via Netlify function during update', error);
            });
          } catch (summaryError) {
            logger.error('Error initiating summary generation during update', summaryError);
          }
        }

        // Format and return the note
        return formatNote(data as NoteData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateNote', noteId },
        });
        throw appError;
      }
    },

    onSuccess: (updatedNote, variables) => {
      const { topicId, noteId } = variables;

      // Update note in cache
      safeUpdateQueryData(queryClient, noteKeys.detail(noteId), () => updatedNote, {
        context: { operation: 'updateNote', noteId },
      });

      // Update note in topic list
      safeUpdateQueryData<FormattedNote[]>(
        queryClient,
        noteKeys.byTopic(topicId),
        notes => {
          if (!notes) return undefined;

          return notes.map(note => (note.id === noteId ? updatedNote : note));
        },
        { context: { operation: 'updateNote', noteId } }
      );
    },
  });
}

/**
 * Hook for deleting a note
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 */
export function useDeleteNote() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ topicId, noteId }: { topicId: string; noteId: string }) => {
      try {
        logger.store('Deleting note', {
          noteId,
          topicId,
          is_online: isOnline,
        });

        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking note as deleted locally', { noteId });

          try {
            // Mark as deleted in IndexedDB
            await db.notes.update(noteId, { isDeleted: true });

            // Queue for sync when online
            await queueOfflineOperation('notes', 'delete', noteId, { id: noteId });

            return { noteId, topicId };
          } catch (offlineError) {
            const error = handleError(offlineError, {
              action: 'toast',
              context: { operation: 'deleteNote', noteId, mode: 'offline' },
            });
            throw error;
          }
        }

        // Online flow - delete from server
        const { error } = await supabase.from('notes').delete().eq('id', noteId);

        if (error) throw error;

        // Remove from IndexedDB
        try {
          await db.notes.delete(noteId);
          logger.fetch(`Deleted note ${noteId} from IndexedDB`);
        } catch (storageError) {
          logger.error(`Error deleting note from IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
          // In a production app, we might want to show a warning to the user or retry
        }

        return { noteId, topicId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'deleteNote', noteId },
        });
        throw appError;
      }
    },

    onSuccess: result => {
      const { noteId, topicId } = result;

      // Remove note from cache
      queryClient.removeQueries({
        queryKey: noteKeys.detail(noteId),
        exact: true,
      });

      // Update the notes list in the cache by filtering out the deleted note
      safeUpdateQueryData<FormattedNote[]>(
        queryClient,
        noteKeys.byTopic(topicId),
        oldData => {
          if (!oldData) return oldData;
          return oldData.filter(note => note.id !== noteId);
        },
        { context: { operation: 'deleteNote', noteId, topicId } }
      );
    },
  });
}

/**
 * Hook for getting filtered notes based on search and user filters
 */
export function useFilteredNotes(topicId: string | null, searchQuery?: string, userIds?: string[]) {
  // Only include filters if they have values
  const filters: NoteFilters = {};
  if (searchQuery?.trim()) filters.searchQuery = searchQuery.trim();
  if (userIds?.length) filters.userIds = userIds;

  // Use the main useNotes hook with filters
  const hasActiveFilters = Object.keys(filters).length > 0;

  return useNotes(topicId, hasActiveFilters ? filters : undefined);
}
